import { useEffect, useRef, useState, type ReactNode } from "react";

type DeferredMountProps = {
  children: ReactNode;
  /** "idle" waits for the browser to go idle after load; "visible" waits for scroll proximity. */
  when: "idle" | "visible";
  /** Reserved space so mounting later does not shift layout. */
  minHeight?: number;
  className?: string;
};

/**
 * Keeps heavy, non-critical subtrees (and their JS chunks) out of the initial
 * render so they don't compete with LCP or add to blocking time.
 */
const DeferredMount = ({ children, when, minHeight, className }: DeferredMountProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;

    if (when === "idle") {
      const schedule = () => {
        if (typeof requestIdleCallback === "function") {
          return requestIdleCallback(() => setMounted(true), { timeout: 3000 });
        }
        return window.setTimeout(() => setMounted(true), 1500);
      };
      let handle: number | undefined;
      if (document.readyState === "complete") {
        handle = schedule() as number;
      } else {
        window.addEventListener("load", () => { handle = schedule() as number; }, { once: true });
      }
      return () => {
        if (handle === undefined) return;
        if (typeof cancelIdleCallback === "function") cancelIdleCallback(handle);
        else clearTimeout(handle);
      };
    }

    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMounted(true);
          observer.disconnect();
        }
      },
      // Start loading before it scrolls into view so it is ready on arrival.
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [when, mounted]);

  return (
    <div ref={ref} className={className} style={minHeight && !mounted ? { minHeight } : undefined}>
      {mounted ? children : null}
    </div>
  );
};

export default DeferredMount;
